import React, { useState } from "react"

import { Add } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  Alert,
  Box,
} from '@mui/material'

import { handleApiError, usersService, type CreateDoctorRequest, type User } from "../../api"

type CreateDoctorDialogProps = {
  onDoctorCreated: (user: User) => void
}

export function CreateDoctorDialog({ onDoctorCreated }: CreateDoctorDialogProps) {
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [formData, setFormData] = useState<CreateDoctorRequest>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
  })

  const handleInputChange = (field: keyof CreateDoctorRequest) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }))
    if (error) setError("")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName || !formData.dateOfBirth) {
      setError("All fields are required")
      return
    }

    try {
      setIsSubmitting(true)
      setError("")

      const response = await usersService.createDoctor(formData)
      onDoctorCreated(response.user)

      // Reset form and close dialog
      setFormData({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        dateOfBirth: "",
      })
      setOpen(false)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setError("")
    setFormData({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      dateOfBirth: "",
    })
  }

  return (
    <>
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => setOpen(true)}
      >
        Create Doctor
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create New Doctor
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add a new doctor user to the system. All fields are required.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  placeholder="doctor@example.com"
                  value={formData.email}
                  onChange={handleInputChange("email")}
                  disabled={isSubmitting}
                  required
                  autoComplete="new-email"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  placeholder="Minimum 8 characters"
                  value={formData.password}
                  onChange={handleInputChange("password")}
                  disabled={isSubmitting}
                  required
                  inputProps={{ minLength: 8 }}
                  autoComplete="new-password"
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleInputChange("firstName")}
                  disabled={isSubmitting}
                  required
                  autoComplete="off"
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleInputChange("lastName")}
                  disabled={isSubmitting}
                  required
                  autoComplete="off"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange("dateOfBirth")}
                  disabled={isSubmitting}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                  autoComplete="off"
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
            >
              {isSubmitting ? "Creating..." : "Create Doctor"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}
